import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type Phaser from 'phaser';
import type { FinishPlayAttemptInput } from '../../../shared/results';
import {
  EventBus,
  ROUTE_GAME_FINISHED_EVENT,
  ROUTE_HUD_UPDATE_EVENT,
  type RouteGameResult,
  type RouteHudState,
  type RouteHudUpdate,
} from '../../game/EventBus';
import { FullscreenGameShell } from '../../game/FullscreenGameShell';
import type { FieryRouteLevel } from './domain/routeLevels';
import { startAlevliRotaGame } from './game/startAlevliRotaGame';
import type { FieryRouteMode } from '../../../shared/fieryRouteModes';

type AlevliRotaGameProps = {
  levels: FieryRouteLevel[];
  label?: string;
  restartEnabled?: boolean;
  /** Normal (`guided`) veya 3 Can (`challenge`). */
  mode?: FieryRouteMode;
  onFinished?: (result: FinishPlayAttemptInput) => void;
};

export function AlevliRotaGame({ levels, label = 'Alevli Rota oyun tuvali', restartEnabled = true, mode = 'challenge', onFinished }: AlevliRotaGameProps) {
  const gameRef = useRef<Phaser.Game | null>(null);
  const onFinishedRef = useRef(onFinished);
  const gameId = `fiery-route-${useId().replace(/:/g, '')}`;
  const containerId = `${gameId}-container`;
  /** Oyun, nasıl oynanır kartı kapatılınca başlar; labirent ve Çiseler öğrenci okurken ilerlemez. */
  const [started, setStarted] = useState(false);
  const [hud, setHud] = useState<RouteHudState>(() => ({
    level: 1,
    totalLevels: levels.length,
    question: levels[0]?.prompt ?? '',
    questionMediaUrl: levels[0]?.promptMediaUrl,
    phase: 'overview',
    score: 0,
    lives: 3,
  }));

  useEffect(() => { onFinishedRef.current = onFinished; }, [onFinished]);

  useLayoutEffect(() => {
    if (!started) return;
    const handleHud = ({ gameId: source, hud: next }: RouteHudUpdate) => { if (source === gameId) setHud(next); };
    const handleFinished = ({ gameId: source, result }: RouteGameResult) => { if (source === gameId) onFinishedRef.current?.(result); };
    EventBus.on(ROUTE_HUD_UPDATE_EVENT, handleHud);
    EventBus.on(ROUTE_GAME_FINISHED_EVENT, handleFinished);
    gameRef.current = startAlevliRotaGame(containerId, gameId, levels, restartEnabled, mode);
    return () => {
      EventBus.off(ROUTE_HUD_UPDATE_EVENT, handleHud);
      EventBus.off(ROUTE_GAME_FINISHED_EVENT, handleFinished);
      gameRef.current?.destroy(true);
      gameRef.current = null;
    };
  }, [containerId, gameId, levels, restartEnabled, started, mode]);

  return (
    <FullscreenGameShell
      gameRef={gameRef}
      className="fiery-route-shell"
      toolbar={<>
        <strong className="toolbar-level">SEVİYE {hud.level} / {hud.totalLevels}</strong>
        <div className="route-question" aria-label={`Soru: ${hud.question}`}>
          {hud.questionMediaUrl && <img src={hud.questionMediaUrl} alt="Soru görseli" />}
          {hud.question && <span>{hud.question}</span>}
        </div>
        <span className="toolbar-score">Puan: {hud.score}</span>
        {mode === 'guided'
          ? <span className="toolbar-lives route-mode-badge">Normal mod</span>
          : <span className="toolbar-lives" aria-label={`${hud.lives} can kaldı`}>
            {'♥ '.repeat(hud.lives)}{'♡ '.repeat(3 - hud.lives)}
          </span>}
      </>}
    >
      <div className="fiery-route-stage">
        <div className="game-container" id={containerId} role="application" aria-label={label} />
        {!started && <section className="route-howto" aria-labelledby={`${gameId}-howto`}>
          <h2 id={`${gameId}-howto`}>Nasıl oynanır?</h2>
          <div className="route-howto-body">
            <div className="route-howto-diagram" aria-hidden="true">
              <span className="route-howto-room"><span className="route-howto-walk">Kıvıl</span><span className="route-howto-target" /></span>
              <small>Alanın içinde dolaşmak seçim değildir.<br />Ortadaki hedefe girince seçersin.</small>
            </div>
            <ul>
              <li>Kıvıl'ı <b>ok tuşlarıyla</b>, <b>WASD</b> ile ya da ekrandaki <b>joystick</b> ile yönet.</li>
              <li>Doğru cevabın yazdığı renkli alana git.</li>
              <li><b>Cevabı seçmek için alanın TAM ORTASINA gir.</b> Alanın içine girince ortada yarısaydam bir hedef belirir; oraya git.</li>
              {mode === 'guided'
                ? <li>Yanlış kapıya girersen üzerinde ✕ çıkar ve doğru kapı sana gösterilir; sonra sıradaki soruya geçersin. Çiseler yakalarsa başa dönersin, oyun bitmez.</li>
                : <li><b>3 can</b> var. Çiselere yakalanırsan ya da yanlış cevaba girersen bir can kaybedersin; canların biterse oyun biter.</li>}
            </ul>
          </div>
          <button type="button" className="route-howto-start" autoFocus onClick={() => setStarted(true)}>Anladım, başla →</button>
        </section>}
      </div>
    </FullscreenGameShell>
  );
}
